/**
 * LenderComparisonPanel
 *
 * Displays credit applications for a deal in a side-by-side card grid,
 * enabling the broker to compare lender offers at a glance.
 *
 * F008 — Multi-Lender Comparison View:
 * AC002: Side-by-side application cards with key metrics (responsive grid)
 * AC003: Best-value highlighting (green for best rate, best amount, fewest conditions)
 * AC004: Conditions summary per application (outstanding, satisfied, total counts)
 * AC005: Layout adapts from 2 to 6 applications
 */

import { useQueries } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Types ────────────────────────────────────────────────────────────────────

interface ApplicationRow {
  id: string;
  dealId: string;
  applicationReference: string | null;
  lenderReference: string | null;
  lenderId: string | null;
  lenderName: string | null;
  lenderProductId: string | null;
  applicationType: string;
  applicationStatus: string;
  requestedAmount: string | null;
  approvedAmount: string | null;
  facilityLimit: string | null;
  proposedRate: string | null;
  approvedRate: string | null;
  assessmentRate: string | null;
  createdDate: string | null;
  submittedDate: string | null;
  decisionDate: string | null;
  approvalExpiryDate: string | null;
  expectedSettlementDate: string | null;
  actualSettlementDate: string | null;
  servicingAtActualRate: string | null;
  servicingAtAssessmentRate: string | null;
  lvrTotal: string | null;
  declineReason: string | null;
  withdrawalReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ConditionRow {
  id: string;
  applicationId: string;
  conditionStatus: string;
  [key: string]: unknown;
}

interface LenderComparisonPanelProps {
  applications: ApplicationRow[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const APPLICATION_STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  pending_review: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200",
  conditionally_approved: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200",
  withdrawn: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  expired: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200",
};

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending Review",
  in_review: "In Review",
  approved: "Approved",
  conditionally_approved: "Conditionally Approved",
  declined: "Declined",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

const APPLICATION_TYPE_LABELS: Record<string, string> = {
  new: "New",
  variation: "Variation",
  refinance: "Refinance",
  top_up: "Top Up",
  restructure: "Restructure",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatRate(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${(num * 100).toFixed(2)}%`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function parseNumericValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
}

// ── Best-Value Computation ──────────────────────────────────────────────────

interface BestValues {
  bestRateId: string | null;
  bestAmountId: string | null;
  fewestConditionsId: string | null;
}

function computeBestValues(
  applications: ApplicationRow[],
  conditionsMap: Map<string, { outstanding: number; satisfied: number; total: number }>
): BestValues {
  let bestRateId: string | null = null;
  let bestRate = Infinity;

  let bestAmountId: string | null = null;
  let bestAmount = -Infinity;

  let fewestConditionsId: string | null = null;
  let fewestOutstanding = Infinity;

  for (const app of applications) {
    // Best rate: lowest proposedRate (lower is better for borrower)
    const rate = parseNumericValue(app.proposedRate);
    if (rate !== null && rate < bestRate) {
      bestRate = rate;
      bestRateId = app.id;
    }

    // Best amount: highest approvedAmount
    const amount = parseNumericValue(app.approvedAmount);
    if (amount !== null && amount > bestAmount) {
      bestAmount = amount;
      bestAmountId = app.id;
    }

    // Fewest outstanding conditions
    const condCounts = conditionsMap.get(app.id);
    if (condCounts && condCounts.outstanding < fewestOutstanding) {
      fewestOutstanding = condCounts.outstanding;
      fewestConditionsId = app.id;
    }
  }

  return { bestRateId, bestAmountId, fewestConditionsId };
}

// ── Metric Row ──────────────────────────────────────────────────────────────

interface MetricRowProps {
  label: string;
  value: string;
  highlight?: boolean;
  testId?: string;
}

function MetricRow({ label, value, highlight, testId }: MetricRowProps) {
  return (
    <div
      className={cn(
        "flex justify-between py-1.5 px-2 text-sm",
        highlight && "bg-green-50 dark:bg-green-950 rounded"
      )}
      data-testid={testId}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-medium", highlight && "text-green-700 dark:text-green-400")}>
        {value}
      </span>
    </div>
  );
}

// ── Conditions count helpers ─────────────────────────────────────────────────

function computeConditionCounts(conditionsData: ConditionRow[] | undefined) {
  const items = conditionsData ?? [];
  const outstanding = items.filter(
    (c) =>
      c.conditionStatus === "outstanding" ||
      c.conditionStatus === "submitted" ||
      c.conditionStatus === "under_review"
  ).length;
  const satisfied = items.filter(
    (c) => c.conditionStatus === "satisfied" || c.conditionStatus === "waived"
  ).length;
  return { outstanding, satisfied, total: items.length };
}

// ── Application Card ────────────────────────────────────────────────────────

interface ApplicationCardProps {
  application: ApplicationRow;
  bestValues: BestValues;
  conditionCounts: { outstanding: number; satisfied: number; total: number };
  conditionsLoading: boolean;
}

function ApplicationCard({
  application,
  bestValues,
  conditionCounts,
  conditionsLoading,
}: ApplicationCardProps) {
  const isBestRate = bestValues.bestRateId === application.id;
  const isBestAmount = bestValues.bestAmountId === application.id;
  const isBestConditions = bestValues.fewestConditionsId === application.id;

  return (
    <Card
      className={cn(
        "flex flex-col",
        (isBestRate || isBestAmount) && "ring-2 ring-green-500 dark:ring-green-400"
      )}
      data-testid={`comparison-card-${application.id}`}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-semibold text-sm truncate">
            {application.lenderName || "No lender"}
          </h4>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Badge variant="outline" className="text-xs">
              {APPLICATION_TYPE_LABELS[application.applicationType] || application.applicationType}
            </Badge>
            <Badge
              variant="secondary"
              className={cn(
                "text-xs",
                APPLICATION_STATUS_COLORS[application.applicationStatus] || "bg-gray-100 text-gray-800"
              )}
            >
              {APPLICATION_STATUS_LABELS[application.applicationStatus] || application.applicationStatus}
            </Badge>
          </div>
        </div>
        {application.applicationReference && (
          <p className="text-xs text-muted-foreground mt-1">
            Ref: {application.applicationReference}
          </p>
        )}
      </CardHeader>

      <CardContent className="flex-1 px-4 pb-4 pt-0 space-y-1">
        {/* Key Metrics */}
        <div className="border-t pt-2">
          <MetricRow
            label="Requested"
            value={formatCurrency(application.requestedAmount)}
          />
          <MetricRow
            label="Approved"
            value={formatCurrency(application.approvedAmount)}
            highlight={isBestAmount}
            testId={isBestAmount ? `best-amount-${application.id}` : undefined}
          />
          <MetricRow
            label="Proposed Rate"
            value={formatRate(application.proposedRate)}
            highlight={isBestRate}
            testId={isBestRate ? `best-rate-${application.id}` : undefined}
          />
          <MetricRow
            label="Approved Rate"
            value={formatRate(application.approvedRate)}
          />
          <MetricRow
            label="Assessment Rate"
            value={formatRate(application.assessmentRate)}
          />
          <MetricRow
            label="LVR"
            value={formatRate(application.lvrTotal)}
          />
          <MetricRow
            label="Servicing (Actual)"
            value={formatCurrency(application.servicingAtActualRate)}
          />
          <MetricRow
            label="Servicing (Assessment)"
            value={formatCurrency(application.servicingAtAssessmentRate)}
          />
        </div>

        {/* Conditions Summary */}
        <div className="border-t pt-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">Conditions</p>
          {conditionsLoading ? (
            <div className="flex items-center gap-1 text-sm text-muted-foreground py-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Loading conditions...</span>
            </div>
          ) : conditionCounts.total === 0 ? (
            <div
              className="text-sm text-muted-foreground py-1"
              data-testid={`conditions-summary-${application.id}`}
            >
              No conditions
            </div>
          ) : (
            <div
              className={cn(
                "flex items-center gap-3 py-1.5 px-2 rounded text-sm",
                isBestConditions && "bg-green-50 dark:bg-green-950"
              )}
              data-testid={`conditions-summary-${application.id}`}
            >
              {conditionCounts.outstanding > 0 && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {conditionCounts.outstanding}
                </span>
              )}
              {conditionCounts.satisfied > 0 && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {conditionCounts.satisfied}
                </span>
              )}
              <span className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                {conditionCounts.total} total
              </span>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="border-t pt-2">
          <p className="text-xs font-medium text-muted-foreground mb-1">Timeline</p>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Submitted</span>
            <span>{formatDate(application.submittedDate)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Decision</span>
            <span>{application.decisionDate ? formatDate(application.decisionDate) : "Pending"}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────

export function LenderComparisonPanel({ applications }: LenderComparisonPanelProps) {
  // Fetch conditions for all applications in parallel using useQueries
  // This is the correct React Query pattern for dynamic parallel queries
  const conditionsResults = useQueries({
    queries: applications.map((app) => ({
      queryKey: [`/api/applications/${app.id}/conditions`],
      enabled: !!app.id,
    })),
  });

  // Build conditions map from query results
  const conditionsMap = new Map<
    string,
    { outstanding: number; satisfied: number; total: number }
  >();
  for (let i = 0; i < applications.length; i++) {
    const result = conditionsResults[i];
    const counts = computeConditionCounts(result?.data as ConditionRow[] | undefined);
    conditionsMap.set(applications[i].id, counts);
  }

  const bestValues = computeBestValues(applications, conditionsMap);

  if (applications.length < 2) {
    return (
      <div
        className="text-center py-8 text-muted-foreground"
        data-testid="comparison-single-message"
      >
        <p>Add more applications to compare lender offers.</p>
      </div>
    );
  }

  return (
    <div data-testid="comparison-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {applications.map((app, idx) => (
          <ApplicationCard
            key={app.id}
            application={app}
            bestValues={bestValues}
            conditionCounts={
              conditionsMap.get(app.id) ?? { outstanding: 0, satisfied: 0, total: 0 }
            }
            conditionsLoading={conditionsResults[idx]?.isLoading ?? false}
          />
        ))}
      </div>
    </div>
  );
}
